import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { firstValueFrom } from "rxjs";
import { environment } from "environments/environment";
import { Product, ReturnRequestDetail } from "../model/entities/entity-model";
import { DialogBaseComponent, DialogParams } from "../shared/dialog-base.component";
import { DomainService } from "../shared/domain.service";

export interface ReconcileRrdDialogParams extends DialogParams {
  rrdId: number;
}

/**
 * Pass 5 dialog for UNRECONCILED → CLOSED.
 *
 * Shows the operator the existing ReconciliationExplanation (set when SETTLING
 * bounced the RRD here) for context, accepts a required resolution note, and
 * on confirm calls RecordReconciliation. Server appends the resolution to
 * ReconciliationExplanation, transitions the RRD to CLOSED, and emits the
 * ReturnReconciled JoNote.
 *
 * No CreditMemos are created here — the assumption is the operator already
 * handled the money outside the pipeline (corrected invoice, manual credit,
 * written-off restock, etc.).
 */
@Component({
  selector: "app-reconcile-rrd-dialog",
  templateUrl: "./reconcile-rrd-dialog.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ReconcileRrdDialogComponent extends DialogBaseComponent<ReconcileRrdDialogParams> implements OnInit {
  rrd: ReturnRequestDetail | null = null;
  product: Product | null = null;
  resolutionNote = "";
  /** Operator's terminal decision — drives the SupplierAccepted/SupplierRejected
   *  signal reported back to Proximity. Required; no default. */
  outcome: "Accept" | "Reject" | null = null;
  submitting = false;
  errorMessage: string | null = null;

  constructor(
    config: DynamicDialogConfig<ReconcileRrdDialogParams>,
    dialogRef: DynamicDialogRef,
    domainService: DomainService,
    private http: HttpClient
  ) {
    super(config, dialogRef, domainService);
  }

  async ngOnInit() {
    const r = await this.uow.fetchEntityByKey(ReturnRequestDetail, this.params.rrdId);
    this.rrd = (r?.entity as ReturnRequestDetail) ?? null;
    if (this.rrd?.joDetail) {
      this.product = await this.dbQueryService.getProductById(this.rrd.joDetail.productId);
    }
    this.isPageReady = true;
  }

  get variantText(): string {
    if (!this.product) return "";
    return (this.product as any).getFeatureChoicesExtract?.() ?? "";
  }

  get canSubmit(): boolean {
    return !this.submitting
      && this.rrd != null
      && this.resolutionNote.trim().length > 0
      && this.outcome != null;
  }

  async confirm() {
    if (!this.canSubmit || !this.rrd || !this.outcome) return;
    this.submitting = true;
    this.errorMessage = null;
    try {
      const actor = this.authService.getUser()?.initials ?? "SYS";
      const url = environment.apiRoot + "breeze/Uniformax/RecordReconciliation";
      await firstValueFrom(this.http.post(url, {
        rrdId: this.rrd.id,
        resolutionNote: this.resolutionNote.trim(),
        outcome: this.outcome,
        actor: actor,
      }));
      this.toastr.success(`RRD #${this.rrd.id} — manually reconciled (${this.outcome}), moved to CLOSED`);
      this.dialogRef.close(true);
    } catch (err: any) {
      const msg = err?.error?.error || err?.message || "Failed to record reconciliation.";
      this.errorMessage = msg;
    } finally {
      this.submitting = false;
    }
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
